import { toast } from 'sonner';
import i18n from '../lib/i18n';
import { ApiError } from './errors';

export type ErrorUxStrategy =
    | 'TOAST'
    | 'INLINE'
    | 'REDIRECT_LOGIN'
    | 'REDIRECT_MFA_SETUP'
    | 'IGNORE_HANDLED_BY_FORM';

// TD-WEBAPP-004: codes faltantes del backend no mapeados. Ver TECH_DEBT.md.
const STRATEGY_BY_CODE: Record<string, ErrorUxStrategy> = {
    SESSION_EXPIRED: 'REDIRECT_LOGIN',
    MFA_REQUIRED: 'REDIRECT_MFA_SETUP',
    INVALID_CREDENTIALS: 'INLINE',
    VALIDATION_ERROR: 'INLINE',
    SCOPE_INVALID_FOR_ROLE: 'INLINE',
    PERMISSION_DENIED: 'TOAST',
    IDEMPOTENCY_CONFLICT: 'TOAST',
    SECURITY_POLICY_VIOLATION: 'TOAST',
    TENANT_SUSPENDED: 'REDIRECT_LOGIN',
    TENANT_NOT_FOUND: 'TOAST',
    TENANT_ALREADY_EXISTS: 'INLINE',
    USER_NOT_FOUND: 'TOAST',
    USER_ALREADY_EXISTS: 'INLINE',
    ROLE_NOT_FOUND: 'TOAST',
    // Destino de la asignación (entidad de nivel) ya no existe (borrado entre el list y el submit).
    LEVEL_ENTITY_NOT_FOUND: 'TOAST',
    MESSAGES_BUSINESS_ERROR: 'TOAST',
    AUDIT_WRITE_ERROR: 'TOAST',
    INTERNAL_ERROR: 'TOAST',
};

// Mensaje propio del front para códigos donde el `msg` del backend no es claro para el usuario.
// Sobrescribe `error.msg` al disparar el TOAST.
const TOAST_MESSAGE_BY_CODE: Record<string, string> = {
    LEVEL_ENTITY_NOT_FOUND: 'La entidad seleccionada no existe o ya no está disponible',
};

function resolveStrategy(error: ApiError): ErrorUxStrategy {
    const code = error.code;
    // Rechazo a nivel API Gateway / authorizer: el body es `{message:"Forbidden"}` (sin el
    // wire-shape Fiado), así que `code` viene undefined. Un 401/403 sin code = sesión inválida
    // o expirada → re-login. (Un 403 con code, p.ej. PERMISSION_DENIED, NO entra acá.)
    if (!code) {
        return error.httpStatus === 401 || error.httpStatus === 403 ? 'REDIRECT_LOGIN' : 'TOAST';
    }
    if (code === 'NO_SESSION') return 'REDIRECT_LOGIN';
    if (Object.prototype.hasOwnProperty.call(STRATEGY_BY_CODE, code)) {
        return STRATEGY_BY_CODE[code];
    }
    if (code.startsWith('COGNITO_')) {
        return code === 'COGNITO_INVALID_OTP' ? 'INLINE' : 'TOAST';
    }
    return 'TOAST';
}

export function handleApiError(error: unknown): ErrorUxStrategy {
    if (!(error instanceof ApiError)) {
        toast.error(i18n.t('errors:unknown'));
        return 'TOAST';
    }

    const strategy = resolveStrategy(error);
    if (strategy === 'TOAST') {
        toast.error(TOAST_MESSAGE_BY_CODE[error.code] ?? error.msg);
    }
    return strategy;
}
